import { inject, Injectable } from '@angular/core';
import { MediaHavenActionType } from '@common/sandboxes/actions/entities/action-types.entity';
import { ActionDisabledCondition, ActionItemOptions, UserAction } from '@mediahaven/shared';
import { ApiFragment, ApplicationConfigService, Mh2Entity, NavigationLink } from '@stylehaven/components';
import { trimEnd, trimStart } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { ActionService } from '../../abstract/action.service.interface';

@Injectable({ providedIn: 'root' })
export class ContactAdminActionService extends ActionService {
    protected override readonly action: MediaHavenActionType = MediaHavenActionType.CONTACT_ADMIN;

    // Services
    private readonly configService: ApplicationConfigService = inject(ApplicationConfigService);

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = false;
    protected readonly ENABLE_FINISHED_TOASTS: boolean = false;
    protected readonly REFRESH_ON_ACTION: boolean = false;
    protected readonly DISABLE_ON_EXECUTE: boolean = false;

    // Properties
    private readonly email: string;
    private readonly baseUrl: string;

    constructor() {
        super();

        const email: string | undefined = this.configService.getInstant('adminEmail');
        const baseUrl: string | undefined = this.configService.getInstant('baseUrl');

        if (!email || !baseUrl) {
            console.warn('ContactAdminActionService:: baseUrl or admin email is not configured');
            return;
        }

        this.email = email;
        this.baseUrl = baseUrl;
    }

    /*  Contact admin
     *  Available when:
     *   * Is published or archived
     *
     *  Disabled when:
     *   * No email is set
     *   * No baseUrl is set
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        return of(this.userActionBuilder(record));
    }

    public userActionBuilder(record: ApiFragment): UserAction {
        const isPublished: boolean = record.isPublished();
        const isArchived: boolean = record.isArchived();
        const isDeleted: boolean = record.isDeleted();

        const availableConditions: boolean[] = [isPublished || isArchived, !isDeleted];
        const disableConditions: ActionDisabledCondition[] = [
            { condition: !this.email, tooltip: 'contact-admin.no-email', entitySpecificTooltip: false },
            { condition: !this.baseUrl, tooltip: 'contact-admin.no-base-url', entitySpecificTooltip: false }
        ];

        const contactAction: UserAction = new UserAction(availableConditions, disableConditions);

        return contactAction;
    }

    protected onAction(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<boolean> {
        this.sendEmail(record);
        return of(true);
    }

    private sendEmail(record: ApiFragment): void {
        const fullUrl = `${trimEnd(this.baseUrl, '/')}/${trimStart(record.getDetailUrl(), '/')}`;
        const content = `${record.getTitle()}:%0D${fullUrl.toString()}%0D`;

        const mailTo = `mailto:${this.email}`;
        const body = `body=${content}`;
        window.location.href = `${mailTo}?${body}`;
    }

    public override getLabelKey(target: ApiFragment): string {
        return 'actions.contact-admin';
    }

    public override getToastLabelKey(target: ApiFragment): string {
        return '';
    }

    public override getToastEntity(target: ApiFragment, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return Mh2Entity.Generic;
    }

    public override getSuccessToastLinkLabelKey(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return '';
    }

    public override getSuccessToastLink(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): NavigationLink | undefined {
        return undefined;
    }

    public override getToastTranslationOptions(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Record<string, string> {
        return {};
    }
}
